import { Recycle } from "lucide-react";
import React, { useState } from "react";
import { Badge } from "../../../../components/ui/common/badge";
import CollapsibleSectionCard from "../shared/components/CollapsibleSectionCard";
import { usePaginatedRecords } from "../shared/hooks/usePaginatedRecords";
import WasteAddRecordPanel from "./components/WasteAddRecordPanel";
import { WasteHeaderTotals, WasteOverTargetWarning } from "./components/WasteHeaderTotals";
import WasteRecordRow from "./components/WasteRecordRow";
import { PAGINATION_ITEMS_PER_PAGE } from "./constants/wasteRecords.constants";
import { useWasteManagementOptions } from "./hooks/useWasteManagementOptions";
import { WastePagination } from "../../../../components/wasteManagment/components/WastePagination";
import { computeWasteTotals, formValuesToRecord } from "./utils/wasteRecords.utils";
import { WasteFormValues, WasteSectionProps } from "./types/wasteRecords.types";

/**
 * Waste & Side Stream sub-section for a process. Composition only — pagination,
 * option lookups, and unit/total arithmetic each live in their own hook/util.
 */
const WasteSection: React.FC<WasteSectionProps> = ({
  records,
  disabled,
  onAdd,
  onUpdate,
  onRemove,
  ingredientOptions = [],
  wasteTarget = "",
  wasteTargetUnit = "kg"
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState(0);

  const { mgmtOptions, mgmtLabelMap } = useWasteManagementOptions();
  const { page, setPage, totalPages, paginated, goToLastPageAfterAdd, clampAfterRemove } =
    usePaginatedRecords(records, PAGINATION_ITEMS_PER_PAGE);

  const totals = computeWasteTotals(records, wasteTarget, wasteTargetUnit);

  const handleAddSave = (values: WasteFormValues) => {
    onAdd(formValuesToRecord(values));
    setDraftKey(k => k + 1);
    goToLastPageAfterAdd();
  };

  const handleEditSave = (id: string) => (values: WasteFormValues) => {
    onUpdate(id, formValuesToRecord(values));
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    onRemove(id);
    setEditingId(null);
    clampAfterRemove();
  };

  return (
    <CollapsibleSectionCard
      icon={Recycle}
      title="Waste & Side Stream"
      disabled={disabled}
      badge={
        records.length > 0 && (
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {records.length}
          </Badge>
        )
      }
      headerRight={<WasteHeaderTotals totals={totals} />}
      headerBelow={<WasteOverTargetWarning totals={totals} />}
    >
      {paginated.map((rec, idx) => (
        <WasteRecordRow
          key={rec.id}
          rec={rec}
          idx={(page - 1) * PAGINATION_ITEMS_PER_PAGE + idx}
          ingredientOptions={ingredientOptions}
          mgmtOptions={mgmtOptions}
          mgmtLabelMap={mgmtLabelMap}
          isEditing={editingId === rec.id}
          onEdit={() => setEditingId(rec.id)}
          onSave={handleEditSave(rec.id)}
          onCancelEdit={() => setEditingId(null)}
          onDelete={() => handleDelete(rec.id)}
        />
      ))}

      <WastePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      <WasteAddRecordPanel
        draftKey={draftKey}
        hasRecords={records.length > 0}
        onSave={handleAddSave}
        ingredientOptions={ingredientOptions}
        mgmtOptions={mgmtOptions}
      />
    </CollapsibleSectionCard>
  );
};

export default WasteSection;
